import { Button } from '@/components/ui/Button'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { PollForm } from './PollForm'
import { useCreatePoll } from './useCreatePoll'
import { endTimeRefinement, pollFormSchema, startTimeRefinement, type PollFormSchema } from './schema'
import { getMaxSupply } from '@/config/get-max-supply.ts'
import { usePepperSupply } from '@/hooks/pepper-contract/usePepperSupply.ts'
import { formatEther } from 'viem'
import type { Hex } from 'viem'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	type ResponsiveDialogProps
} from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'

const DEFAULT_VOTING_CAP = 50_000_000_000_000

const MONDAY = 1 // JS getDay(): 0=Sunday..6=Saturday
const DEFAULT_POLL_HOUR = 11
const DAYS_MONDAY_TO_THURSDAY = 3

function getSecondUpcomingMondayAt(hour: number): Date {
	const now = new Date()
	const daysUntilNextMonday = (MONDAY - now.getDay() + 7) % 7 || 7
	const result = new Date(now)
	result.setDate(now.getDate() + daysUntilNextMonday + 7)
	result.setHours(hour, 0, 0, 0)
	return result
}

function getDefaultPollTimes(): { startTime: Date; endTime: Date } {
	const startTime = getSecondUpcomingMondayAt(DEFAULT_POLL_HOUR)
	const endTime = new Date(startTime)
	endTime.setDate(startTime.getDate() + DAYS_MONDAY_TO_THURSDAY)
	return { startTime, endTime }
}

export function CreatePollDialog({
	onSubmit,
	...dialogProps
}: Omit<ResponsiveDialogProps, 'children'> & { onSubmit: (surveyAddress: Hex) => void }) {
	const { maxSupply } = usePepperSupply()
	const maxVotingCap = maxSupply.status === 'success' ? parseInt(formatEther(maxSupply.data)) : getMaxSupply()
	const form = useForm<PollFormSchema>({
		resolver: zodResolver(
			pollFormSchema(maxVotingCap).superRefine((data, ctx) => {
				startTimeRefinement(data, ctx)
				endTimeRefinement(data, ctx)
			})
		),
		defaultValues: {
			question: 'WHAT DO WE DO WITH PEPPER TREASURY?',
			answers: [
				{ value: 'Buy and burn $PEPPER' },
				{ value: 'Move into CEX Fund & Stake in Chiliz Governance' },
				{ value: 'Buy $PEPPER and move into CEX Fund' },
				{ value: 'Do nothing, let the CHZ pile up & wait for better market conditions' }
			],
			votingCap: DEFAULT_VOTING_CAP,
			...getDefaultPollTimes()
		}
	})

	const { createPoll } = useCreatePoll()

	const onClickSubmit = form.handleSubmit(async values => {
		const surveyAddress = await createPoll({
			question: values.question,
			answers: values.answers.map(answer => answer.value),
			votingCap: values.votingCap,
			startTime: values.startTime,
			endTime: values.endTime
		})

		if (surveyAddress) onSubmit(surveyAddress)
	})

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="overflow-y-auto flex flex-col gap-8">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>Create Poll</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>

					<div className="max-h-[500px] overflow-y-auto relative">
						<PollForm form={form} variant="new" />
					</div>

					<ResponsiveDialogFooter>
						<Button
							onClick={() => {
								onClickSubmit()
							}}
							variant="danger"
							loading={form.formState.isSubmitting}
							className="py-5 px-10 leading-none"
							data-testid={testIdFactory.adminPollConfirmCreationFormButton()}
						>
							Confirm
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
